import { Injectable } from "@angular/core";
import { LoadSolutionMatrix } from "@app/store/actions/solution.actions";
import { selectIsSolutionMatrixLoading, selectSolutionMatrix } from "@app/store/selectors/app/solution.selectors";
import { MainState } from "@app/store/state";
import { select, Store } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { filter, switchMap, take } from "rxjs/operators";

export interface AdvancedSolutionMatrix {
  matrixRect: string;
  matrixDelta: number;
  raMatrix: string;
  decMatrix: string;
}

@Injectable({
  providedIn: "root"
})
export class SolvedCoordinatesService {
  constructor(private readonly store$: Store<MainState>) {}

  /**
   * Ensures that a solution matrix is loaded and returns it as an Observable
   * @param solutionId - The ID of the solution to load the matrix for
   * @returns An Observable that emits the matrix when it's available
   */
  ensureMatrixLoaded(solutionId: number): Observable<AdvancedSolutionMatrix> {
    // First check if the matrix is already in the store
    return this.store$.pipe(
      select(selectSolutionMatrix, solutionId),
      take(1),
      switchMap(matrixFromStore => {
        if (matrixFromStore) {
          // If matrix is already in store, return it
          return of(matrixFromStore);
        } else {
          // Otherwise, check if it's currently being loaded by another component
          return this.store$.pipe(
            select(selectIsSolutionMatrixLoading, solutionId),
            take(1),
            switchMap(isLoading => {
              if (isLoading) {
                // If already loading, wait for it to complete
                return this.store$.pipe(
                  select(selectSolutionMatrix, solutionId),
                  filter(matrix => !!matrix), // Wait until matrix is available
                  take(1)
                );
              } else {
                // If not loading, dispatch action to load it
                this.store$.dispatch(new LoadSolutionMatrix({ solutionId }));

                // Then wait for it to complete
                return this.store$.pipe(
                  select(selectSolutionMatrix, solutionId),
                  filter(matrix => !!matrix), // Wait until matrix is available
                  take(1)
                );
              }
            })
          );
        }
      })
    );
  }
}
